import type { RoutingSkuTargetRecord } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { joinIdentitySegments, targetIdentitySegments } from '../tenantRoutingShared.ts';
import type { TargetShare } from './firstAttemptShare.ts';

/**
 * The selected SKU's first-attempt split, at the top of its target pool
 * (#1495).
 *
 * This is the answer to the question the page exists to ask — "this SKU fans
 * out to which upstreams, in what proportion" — which previously existed only
 * as a `weight` column an operator had to normalise in their head.
 *
 * On colour: the Deck palette (`src/ui/tokens.ts`) has no categorical hues, and
 * the three lamp colours are status semantics that would misread as health if
 * borrowed for identity. So every segment is the same `signal` orange, split by
 * hairline gaps, and identity comes from the legend text below — which also
 * means the bar extends to any number of targets instead of running out of
 * distinct colours at three. The bar itself is `aria-hidden` decoration
 * (DESIGN.md §9); the legend is the readable copy.
 */

interface Props {
  /** Targets in the pool's display order — segments follow the rows below. */
  readonly targets: readonly RoutingSkuTargetRecord[];
  readonly shares: readonly TargetShare[];
  readonly skuId: string;
}

/**
 * Legend labels, disambiguated only as far as they need to be.
 *
 * A pool row is a (upstream, account, key bucket) tuple, so the channel slug
 * alone can label two different segments identically — and since every segment
 * is the same colour, an ambiguous label leaves nothing at all to tell them
 * apart. Appending only the account is not enough either: two buckets of ONE
 * account (rate-limit isolation, a real configuration) differ solely in the
 * last segment.
 *
 * So each colliding group escalates one segment at a time, up to the full
 * identity, and groups that are already unique keep the bare slug rather than
 * carrying disambiguation noise they do not need.
 */
function legendLabels(
  targets: readonly RoutingSkuTargetRecord[],
): ReadonlyMap<string, string> {
  const segments = new Map(
    targets.map((target) => [
      target.id,
      targetIdentitySegments(target, target.upstream_channel_slug),
    ]),
  );
  const depth = new Map(targets.map((target) => [target.id, 1]));
  const labelAt = (id: string): string =>
    joinIdentitySegments((segments.get(id) ?? []).slice(0, depth.get(id) ?? 1));
  const maxDepth = Math.max(...targets.map((target) => segments.get(target.id)?.length ?? 1), 1);

  for (let round = 1; round < maxDepth; round += 1) {
    const idsByLabel = new Map<string, string[]>();
    for (const target of targets) {
      const label = labelAt(target.id);
      idsByLabel.set(label, [...(idsByLabel.get(label) ?? []), target.id]);
    }
    let escalated = false;
    for (const ids of idsByLabel.values()) {
      if (ids.length === 1) continue;
      for (const id of ids) {
        const current = depth.get(id) ?? 1;
        if (current < maxDepth) {
          depth.set(id, current + 1);
          escalated = true;
        }
      }
    }
    if (!escalated) break;
  }

  return new Map(targets.map((target) => [target.id, labelAt(target.id)]));
}

export function TenantRoutingPoolShareBar({ targets, shares, skuId }: Props): JSX.Element {
  const t = useT();
  const labels = legendLabels(targets);
  const targetsById = new Map(targets.map((target) => [target.id, target]));

  const active = shares.filter((share) => share.kind === 'active');
  const reserveCount = shares.filter((share) => share.kind === 'reserve').length;

  return (
    <div
      className="flex flex-col gap-1.5 border-b border-line-soft/50 px-4 pb-3 pt-1"
      data-testid={`tenant-routing-pool-share-${skuId}`}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="text-label text-paper-mid">{t('tenant_routing.pool.share_title')}</span>
        <span className="text-micro text-paper-low">{t('tenant_routing.pool.share_hint')}</span>
      </div>

      {active.length === 0 ? (
        <p className="m-0 text-micro text-paper-low" data-testid={`tenant-routing-pool-share-none-${skuId}`}>
          {t('tenant_routing.pool.share_none')}
        </p>
      ) : (
        <>
          {/*
            `gap-px` over the container's own hairline fill is what separates
            adjacent segments: same colour on both sides, so the boundary is a
            slit rather than a hue change. The gaps make the children total
            slightly over 100%, which `overflow-hidden` clips — at most one
            pixel per segment, well under the rounding the percentages already
            carry.
          */}
          <div className="flex h-2 gap-px overflow-hidden rounded-full bg-line-soft" aria-hidden="true">
            {active.map((share) => (
              <div
                key={share.targetId}
                className="h-full flex-none bg-signal first:rounded-l-full last:rounded-r-full"
                style={{ width: `${String(share.percent)}%` }}
              />
            ))}
          </div>

          <ul className="m-0 flex list-none flex-wrap gap-x-3 gap-y-0.5 p-0">
            {active.map((share) => {
              const target = targetsById.get(share.targetId);
              const label = labels.get(share.targetId) ?? share.targetId;
              return (
                <li
                  key={share.targetId}
                  className="flex items-baseline gap-1 text-micro"
                  data-testid={`tenant-routing-pool-share-legend-${share.targetId}`}
                  title={t('tenant_routing.pool.share_label', {
                    channel: target?.upstream_channel_slug ?? label,
                    percent: share.percent,
                  })}
                >
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-signal" />
                  <span
                    className="font-mono text-paper-mid"
                    data-testid={`tenant-routing-pool-share-name-${share.targetId}`}
                  >
                    {label}
                  </span>
                  <span className="font-mono text-paper">{share.percent}%</span>
                </li>
              );
            })}
          </ul>
        </>
      )}

      {reserveCount > 0 ? (
        <p className="m-0 text-micro text-paper-low" data-testid={`tenant-routing-pool-share-reserve-${skuId}`}>
          {t('tenant_routing.pool.share_reserve_count', { count: reserveCount })}
        </p>
      ) : null}
    </div>
  );
}
